import React, { useEffect, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import * as Fa from "react-icons/fa";
import { toast } from "react-toastify";
import { fetchData, postData } from "../../../Services/GlobalService";
import Loader from "../../../Components/Loader";

/* ============================================================
   TYPES
============================================================ */
export type SegmentType = "B2C" | "HDSSD" | "Industrial DB";

const CHANNEL_TYPE_MAP: Record<SegmentType, string> = {
  "B2C": "B2C",
  "HDSSD": "HDSSD",
  "Industrial DB": "IDB",
};

interface ConstructOption {
  label: string;  // Name — displayed in dropdown
  value: number;  // Seq — sent in payload
}

/* ============================================================
   COMPONENT
============================================================ */
const SIPReProcess: React.FC = () => {
  const UserId = localStorage.getItem("UserId")?.toString();

  const BaseUrl = "DBScoreCard/SIPReProcess";
  const Endpoint = {
    GetConstructList: "GetParameterByConstructSeqId",
    ReProcessSIP: "SIPReprocess",
  };

  /* ── State ── */
  const [segment, setSegment] = useState<SegmentType>("B2C");
  const [selectedConstruct, setSelectedConstruct] = useState<ConstructOption | null>(null);
  const [finalFlag, setFinalFlag] = useState<boolean>(false);
  const [constructList, setConstructList] = useState<ConstructOption[]>([]);
  const [constructLoading, setConstructLoading] = useState<boolean>(false);
  const [reProcessLoading, setReProcessLoading] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<any>({});

  /* ── Reload construct list when segment changes ── */
  useEffect(() => {
    setSelectedConstruct(null);
    setConstructList([]);
    loadConstructList();
  }, [segment]);

  const loadConstructList = async () => {
    setConstructLoading(true);
    try {
      const channelType = CHANNEL_TYPE_MAP[segment];
      const res = await fetchData(
        BaseUrl,
        Endpoint.GetConstructList,
        `ChannelType=${channelType}`
      );
      const list: ConstructOption[] = (res.Data ?? []).map((d: any) => ({
        label: d.Name,
        value: d.Seq,
      }));
      setConstructList(list);
    } catch (ex: any) {
      toast.error(ex?.response?.data?.Message ?? "Failed to load constructs");
    } finally {
      setConstructLoading(false);
    }
  };

  /* ── Validation ── */
  const validateForm = () => {
    const newErrors: any = {};
    if (!selectedConstruct) newErrors.selectedConstruct = "Please select a Construct";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ── Re-Process ── */
  const handleReProcess = async () => {
    setSubmitted(true);
    if (!validateForm()) return;

    setReProcessLoading(true);
    try {
      const payload = {
        channelType: CHANNEL_TYPE_MAP[segment],
        flag: finalFlag ? "Y" : "N",
        constructSeq: String(selectedConstruct),
        userId: UserId ?? "",
      };
      const res = await postData(BaseUrl, Endpoint.ReProcessSIP, payload);
      toast.success(res.Message ?? "Re-Process triggered successfully.");
    } catch (ex: any) {
      toast.error(ex?.response?.data?.Message ?? "Re-Process failed");
    } finally {
      setReProcessLoading(false);
    }
  };

  /* ============================================================
     RENDER
  ============================================================ */
  return (
    <>
      <main>
        <div className="col-12 mb-5">
          <div className="row">
            {/* Breadcrumb */}
            <div className="col-lg-12 col-md-12 col-sm-12">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb mx-0">
                  <li className="breadcrumb-item">DB SIP</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    SIP Re-Process
                  </li>
                </ol>
              </nav>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12">
              <h3 className="py-2">SIP Re-Process</h3>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12"></div>
          </div>

          <section className="sales-box">
            <div className="row d-flex justify-content-center align-items-center">
              <div className="col-lg-5 col-md-12 col-sm-12">
                <form>
                  {/* Segment radio buttons */}
                  <div className="col-lg-12 col-md-12 col-sm-12 mb-2">
                    <div className="d-flex justify-content-start flex-wrap gap-3 mb-3">
                      {(["B2C", "HDSSD", "Industrial DB"] as SegmentType[]).map((s) => (
                        <div key={s} className="d-inline-flex align-items-center">
                          <RadioButton
                            inputId={`seg-${s}`}
                            name="segment"
                            value={s}
                            onChange={(e) => setSegment(e.value)}
                            checked={segment === s}
                          />
                          <label htmlFor={`seg-${s}`} className="form-label ms-2 mb-0">
                            {s}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Construct dropdown */}
                  <div className="col-lg-12 col-md-12 col-sm-12 mb-3">
                    <div className="d-block">
                      <label className="form-label">Select Construct</label>
                      <Dropdown
                        value={selectedConstruct}
                        onChange={(e) => {
                          setSelectedConstruct(e.value);
                          setErrors({ ...errors, selectedConstruct: null });
                        }}
                        options={constructList}
                        optionLabel="label"
                        placeholder={constructLoading ? "Loading..." : "Select Construct"}
                        disabled={constructLoading}
                        className={
                          submitted && errors.selectedConstruct
                            ? "p-invalid w-100"
                            : "w-100"
                        }
                      />
                      {submitted && errors.selectedConstruct && (
                        <small className="p-error">{errors.selectedConstruct}</small>
                      )}
                    </div>
                  </div>

                  {/* Final Flag checkbox */}
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <div className="d-block">
                      <div className="d-flex justify-content-start align-items-start">
                        <Checkbox
                          inputId="finalFlag"
                          checked={finalFlag}
                          onChange={(e: any) => setFinalFlag(e.checked)}
                        />
                        <label htmlFor="finalFlag" className="form-label ms-2 mb-0 fw-bold">
                          Final Flag
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12">
                    <div className="text-end mt-4">
                      <button
                        type="button"
                        className="btn anime-red-btn"
                        onClick={handleReProcess}
                        disabled={reProcessLoading}
                      >
                        {reProcessLoading ? (
                          <>
                            Processing...{" "}
                            <span
                              className="spinner-border spinner-border-sm ms-1"
                              role="status"
                              aria-hidden="true"
                            />
                          </>
                        ) : (
                          "Re-Process"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <div className="col-lg-7 col-md-12 col-sm-12">
                <div className="text-end">
                  <img
                    src={require("../../../Assets/Images/data-processing.png")}
                    className="Side_Img drop-right"
                    alt=""
                    style={{ width: "230px", maxWidth: "100%" }}
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default SIPReProcess;